import { Badge, Input, Pagination, Progress, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useAuth, useDebounce, usePagination, useWorkflowDefinitions, useWorkflowRunsForWorkItem } from '@/hooks'
import { Permissions } from '@/lib/permissions'
import { formatDurationMs } from '@/lib/timeFormatters'
import { getInitials } from '@/lib/utils'
import { type WorkflowRun, type WorkflowRunStatus } from '@/types'
import { Loader2, Play, Search } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChainBadge, WorkflowStatusBadge, workflowRunStatusProgressColors, workflowRunStatusFilterOptions } from './shared'
import { WorkItemChainRuns } from './WorkItemChainRuns'

interface WorkflowHistoryTabProps {
  projectId: string
  workItemId: string
}

function WorkflowHistoryRow({ run, projectId, canViewChainRuns }: { run: WorkflowRun; projectId: string; canViewChainRuns: boolean }) {
  const navigate = useNavigate()
  const phaseProgress = run.totalPhases > 0 ? (run.completedPhases / run.totalPhases) * 100 : 0
  const effectiveStatus = run.status

  return (
    <TableRow
      data-testid="workflow-history-row"
      className="cursor-pointer hover:bg-muted/50"
      onClick={() => navigate(`/projects/${projectId}/workflows/runs/${run.id}`)}
    >
      <TableCell>
        <div className="flex items-center gap-2">
          <span className="min-w-0">{run.definitionDisplayName}</span>
          <Badge variant="outline" className="text-xs shrink-0">V{run.definitionVersion}</Badge>
          {run.chainRunId && (canViewChainRuns ? (
            <span onClick={(e) => e.stopPropagation()}>
              <ChainBadge
                name={run.chainDisplayName ?? 'Chain'}
                onClick={() => navigate(`/projects/${projectId}/workflows/runs/chains/${run.chainRunId}`)}
              />
            </span>
          ) : (
            <ChainBadge name={run.chainDisplayName ?? 'Chain'} />
          ))}
        </div>
      </TableCell>

      <TableCell>
        <WorkflowStatusBadge status={effectiveStatus} />
      </TableCell>

      <TableCell>
        <div className="flex items-center gap-2">
          <Progress value={phaseProgress} className="h-[4px] w-[143px] rounded-[4px]" indicatorClassName={workflowRunStatusProgressColors[effectiveStatus]} />
          <span className="text-xs text-muted-foreground whitespace-nowrap">
            {run.completedPhases}/{run.totalPhases}
          </span>
        </div>
      </TableCell>

      <TableCell className="text-sm">
        {formatDurationMs(run.durationMs)}
      </TableCell>

      <TableCell className="text-sm">
        {formatDurationMs(run.leadTimeMs)}
      </TableCell>

      <TableCell>
        <div className="flex items-center gap-2">
          <div className="h-10 w-10 rounded-full flex items-center justify-center text-white text-xs font-medium shrink-0 bg-slate-700">
            {getInitials(run.triggeredBy)}
          </div>
          <div className="flex min-w-0 flex-col" data-testid="stacked-identity">
            <span className="text-sm font-medium">{run.triggeredBy}</span>
            {run.triggeredByEmail && (
              <span className="text-xs text-muted-foreground">{run.triggeredByEmail}</span>
            )}
          </div>
        </div>
      </TableCell>
    </TableRow>
  )
}

export function WorkflowHistoryTab({ projectId, workItemId }: WorkflowHistoryTabProps) {
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState<WorkflowRunStatus | ''>('')
  const [definitionName, setDefinitionName] = useState('_all')
  const { page, pageSize, paginationParams, setPage, setPageSize } = usePagination()
  const { hasPermission } = useAuth(projectId)
  const canViewChainRuns = hasPermission(Permissions.Project.Chain.View, projectId)
  const debouncedSearch = useDebounce(search)

  const filters = {
    search: debouncedSearch || undefined,
    statuses: status ? [status] : undefined,
    definitionName: definitionName !== '_all' ? definitionName : undefined,
  }

  const { data, isLoading } = useWorkflowRunsForWorkItem(projectId, workItemId, paginationParams, filters)
  const runs = data?.items ?? []
  const totalCount = data?.totalCount ?? 0

  const { data: definitionsData } = useWorkflowDefinitions(
    projectId,
    { maxResultCount: 100, skipCount: 0 },
    { status: 'active', search: '', type: 'all', category: 'all' },
  )
  const definitions = definitionsData?.items ?? []

  const chainRuns = canViewChainRuns ? <WorkItemChainRuns projectId={projectId} workItemId={workItemId} /> : null

  if (!isLoading && totalCount === 0 && !debouncedSearch && !status && definitionName === '_all') {
    return (
      <div className="space-y-4">
        {chainRuns}
        <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="workflow-history-empty">
          <Play className="h-12 w-12 text-muted-foreground/50 mb-4" />
          <h3 className="text-lg font-semibold mb-1">No workflow runs yet</h3>
          <p className="text-sm text-muted-foreground">
            Trigger a workflow to get started.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {chainRuns}
      <h2 className="text-2xl font-medium text-card-foreground">Workflow History</h2>
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="The list is searchable by workflow name, user, work item"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1) }}
            className="pl-9"
            aria-label="Search workflow runs"
          />
        </div>

        <Select value={status || '_all'} onValueChange={(v) => { setStatus(v === '_all' ? '' : v as WorkflowRunStatus); setPage(1) }}>
          <SelectTrigger className="w-[180px]" aria-label="Filter by status">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            {workflowRunStatusFilterOptions.map(s => (
              <SelectItem key={s.value || '_all'} value={s.value || '_all'}>{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={definitionName} onValueChange={(v) => { setDefinitionName(v); setPage(1) }}>
          <SelectTrigger className="w-[200px]" aria-label="Filter by workflow">
            <SelectValue placeholder="All Workflows" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="_all">All Workflows</SelectItem>
            {definitions.map(def => (
              <SelectItem key={def.name} value={def.name}>{def.displayName}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-label="Loading workflow runs" />
        </div>
      ) : runs.length === 0 ? (
        <div className="rounded-lg border border-dashed p-8 text-center">
          <p className="text-sm text-muted-foreground">No workflow runs match your filters.</p>
        </div>
      ) : (
        <div className="rounded-md border">
          <Table aria-label="Workflow history">
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead className="w-[120px]">Status</TableHead>
                <TableHead className="w-[170px]">Phases</TableHead>
                <TableHead className="w-[90px]">Duration</TableHead>
                <TableHead className="w-[90px]">Lead time</TableHead>
                <TableHead className="w-[180px]">User</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {runs.map(run => (
                <WorkflowHistoryRow key={run.id} run={run} projectId={projectId} canViewChainRuns={canViewChainRuns} />
              ))}
            </TableBody>
          </Table>
          <Pagination
            page={page}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        </div>
      )}
    </div>
  )
}
